import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import {
  Text,
  Card,
  IconButton,
  Chip,
  useTheme,
  Divider,
} from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { AIExplanationDisplayProps } from './JourneyPreviewTypes';
import { formatExplanationForA11y } from '@coffeeplexity/shared/utils/ai-explanations';

export function AIExplanationDisplay({
  reasoning,
  showConfidence = true,
  showFactors = true,
  expandable = true,
  style,
}: AIExplanationDisplayProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    if (expandable) {
      setIsExpanded(!isExpanded);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return theme.colors.primary;
    if (confidence >= 0.6) return theme.colors.secondary;
    if (confidence >= 0.4) return '#FFC107';
    return theme.colors.error;
  };

  return (
    <Card style={[styles.card, style]} mode='outlined'>
      <Card.Content>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <Text variant='titleMedium' style={styles.title}>
              AI Reasoning
            </Text>
            {showConfidence && (
              <Chip
                icon='brain'
                mode='outlined'
                compact
                style={[
                  styles.confidenceChip,
                  { borderColor: getConfidenceColor(reasoning.confidence) },
                ]}
                textStyle={[
                  styles.confidenceChipText,
                  { color: getConfidenceColor(reasoning.confidence) },
                ]}
              >
                {Math.round(reasoning.confidence * 100)}% confident
              </Chip>
            )}
          </View>
          {expandable && (
            <IconButton
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              onPress={toggleExpanded}
              accessibilityLabel={
                isExpanded ? 'Collapse explanation' : 'Expand explanation'
              }
            />
          )}
        </View>

        {/* Primary Explanation */}
        <Text
          variant='bodyMedium'
          style={styles.primaryExplanation}
          accessibilityLabel={formatExplanationForA11y({
            primary: reasoning.skillLevelJustification,
            factors: reasoning.primaryFactors,
            confidence: reasoning.confidence,
          })}
        >
          {reasoning.skillLevelJustification}
        </Text>

        {/* Confidence Indicator */}
        {showConfidence && (
          <View style={styles.confidenceSection}>
            <ConfidenceIndicator
              confidence={reasoning.confidence}
              showLabel={true}
              showDescription={true}
              size='medium'
            />
          </View>
        )}

        {/* Key Factors */}
        {showFactors && reasoning.primaryFactors.length > 0 && (
          <View style={styles.factorsSection}>
            <Text variant='labelMedium' style={styles.sectionLabel}>
              Key Factors
            </Text>
            <View style={styles.factorsList}>
              {reasoning.primaryFactors
                .slice(0, isExpanded ? undefined : 3)
                .map((factor, index) => (
                  <View key={index} style={styles.factorItem}>
                    <Text variant='bodySmall' style={styles.factorText}>
                      • {factor}
                    </Text>
                  </View>
                ))}
              {!isExpanded && reasoning.primaryFactors.length > 3 && (
                <Text variant='bodySmall' style={styles.moreFactors}>
                  +{reasoning.primaryFactors.length - 3} more factors
                </Text>
              )}
            </View>
          </View>
        )}

        {/* Expanded Details */}
        {isExpanded && (
          <View style={styles.expandedContent}>
            <Divider style={styles.divider} />

            {/* Duration Rationale */}
            <View style={styles.detailSection}>
              <Text variant='labelMedium' style={styles.detailLabel}>
                Duration Reasoning
              </Text>
              <Text variant='bodySmall' style={styles.detailText}>
                {reasoning.durationRationale}
              </Text>
            </View>

            {/* Content Selection */}
            <View style={styles.detailSection}>
              <Text variant='labelMedium' style={styles.detailLabel}>
                Content Selection Strategy
              </Text>
              <Text variant='bodySmall' style={styles.detailText}>
                {reasoning.contentSelectionReasoning}
              </Text>
            </View>

            {/* Difficulty Progression */}
            <View style={styles.detailSection}>
              <Text variant='labelMedium' style={styles.detailLabel}>
                Difficulty Progression
              </Text>
              <Text variant='bodySmall' style={styles.detailText}>
                {reasoning.difficultyProgression}
              </Text>
            </View>

            {/* Personalized Elements */}
            {reasoning.personalizedElements.length > 0 && (
              <View style={styles.detailSection}>
                <Text variant='labelMedium' style={styles.detailLabel}>
                  Personalized For You
                </Text>
                <View style={styles.personalizedList}>
                  {reasoning.personalizedElements.map((element, index) => (
                    <View key={index} style={styles.personalizedItem}>
                      <Text variant='bodySmall' style={styles.personalizedText}>
                        ✨ {element}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Technical Details */}
            <View style={styles.technicalSection}>
              <Text variant='labelSmall' style={styles.technicalLabel}>
                Technical Details
              </Text>
              <View style={styles.technicalDetails}>
                <Text variant='bodySmall' style={styles.technicalText}>
                  Confidence Score: {(reasoning.confidence * 100).toFixed(1)}%
                </Text>
                <Text variant='bodySmall' style={styles.technicalText}>
                  Factors Analyzed: {reasoning.primaryFactors.length}
                </Text>
                <Text variant='bodySmall' style={styles.technicalText}>
                  Personalization Level:{' '}
                  {reasoning.personalizedElements.length > 0
                    ? 'High'
                    : 'Standard'}
                </Text>
              </View>
            </View>
          </View>
        )}
      </Card.Content>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    marginVertical: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  title: {
    fontWeight: '600',
    flex: 1,
  },
  confidenceChip: {
    height: 24,
  },
  confidenceChipText: {
    fontSize: 10,
    fontWeight: '500',
  },
  primaryExplanation: {
    lineHeight: 20,
    marginBottom: 12,
  },
  confidenceSection: {
    marginBottom: 16,
  },
  factorsSection: {
    marginBottom: 12,
  },
  sectionLabel: {
    fontWeight: '600',
    marginBottom: 8,
    opacity: 0.8,
  },
  factorsList: {
    marginLeft: 8,
  },
  factorItem: {
    marginBottom: 4,
  },
  factorText: {
    lineHeight: 16,
    opacity: 0.8,
  },
  moreFactors: {
    fontStyle: 'italic',
    opacity: 0.6,
    marginTop: 4,
  },
  expandedContent: {
    marginTop: 8,
  },
  divider: {
    marginVertical: 12,
  },
  detailSection: {
    marginBottom: 16,
  },
  detailLabel: {
    fontWeight: '600',
    marginBottom: 6,
    opacity: 0.8,
  },
  detailText: {
    lineHeight: 18,
    opacity: 0.9,
  },
  personalizedList: {
    marginLeft: 8,
  },
  personalizedItem: {
    marginBottom: 4,
  },
  personalizedText: {
    lineHeight: 16,
    opacity: 0.9,
    fontStyle: 'italic',
  },
  technicalSection: {
    marginTop: 8,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 8,
  },
  technicalLabel: {
    fontWeight: '600',
    marginBottom: 6,
    opacity: 0.6,
    textTransform: 'uppercase',
  },
  technicalDetails: {
    gap: 2,
  },
  technicalText: {
    opacity: 0.7,
    fontSize: 11,
    fontFamily: 'monospace',
  },
});
